import * as React from 'react';
import { Toast, type ToastSeverity } from './toast';
import { registerToastDispatch } from '../../lib/toast';

interface ToastEntry {
    id: number;
    severity: ToastSeverity;
    message: string;
}

const AUTO_DISMISS_MS = 5000;

// Zero-width space appended to force a live-region text change when the same
// message fires twice in a row (e.g. two "Link copied" toasts), so screen
// readers re-announce it. Invisible; not read aloud.
const ZWSP = '​';

/**
 * ToastProvider — owns the toast queue and renders a fixed-position container
 * at the top of the viewport. Multiple concurrent toasts stack downward with
 * a small gap; a new toast does not overwrite a visible one.
 *
 * Consumers don't import this directly. Use the imperative API:
 *
 *   import { toast } from '../../lib/toast';
 *   toast.success('Link copied to clipboard');
 *
 * The provider registers its dispatch with the lib/toast module on mount,
 * which lets `toast.success(...)` work from any module — inside or outside
 * a React tree — provided one ToastProvider is mounted somewhere up-tree.
 *
 * Position matches the prior in-line toast implementations: fixed top-20,
 * horizontally centered. Auto-dismiss after 5s; manual dismiss via the
 * IconButton inside each Toast.
 *
 * Accessibility: the visible bubbles are presentational; announcement happens
 * through two PERSISTENT visually-hidden live regions (polite + assertive) that
 * are always mounted, so screen readers reliably pick up new toasts (a live
 * region created together with its content is often missed). Error toasts are
 * assertive (interrupt); success/info are polite.
 */
export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [toasts, setToasts] = React.useState<ToastEntry[]>([]);
    // Text mirrored into the persistent live regions below for screen-reader
    // announcement (separate from the visible bubbles, which are presentational).
    const [politeMsg, setPoliteMsg] = React.useState('');
    const [assertiveMsg, setAssertiveMsg] = React.useState('');
    const idCounterRef = React.useRef(0);
    const announceCountRef = React.useRef(0);
    const timersRef = React.useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

    const remove = React.useCallback((id: number) => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
        const timer = timersRef.current.get(id);
        if (timer) {
            clearTimeout(timer);
            timersRef.current.delete(id);
        }
    }, []);

    const add = React.useCallback((input: { severity: ToastSeverity; message: string }) => {
        idCounterRef.current += 1;
        const id = idCounterRef.current;
        const entry: ToastEntry = { id, ...input };
        setToasts((prev) => [...prev, entry]);
        // Announce via the persistent live regions (below); toggle the ZWSP so an
        // identical consecutive message still registers as a change and re-announces.
        announceCountRef.current += 1;
        const announced = input.message + (announceCountRef.current % 2 ? ZWSP : '');
        if (input.severity === 'error') setAssertiveMsg(announced);
        else setPoliteMsg(announced);
        const timer = setTimeout(() => remove(id), AUTO_DISMISS_MS);
        timersRef.current.set(id, timer);
    }, [remove]);

    React.useEffect(() => {
        const unregister = registerToastDispatch(add);
        return unregister;
    }, [add]);

    React.useEffect(() => {
        // Clear all pending timers on unmount to avoid setState-after-unmount.
        const timers = timersRef.current;
        return () => {
            timers.forEach((t) => clearTimeout(t));
            timers.clear();
        };
    }, []);

    return (
        <>
            {children}
            {/* Persistent visually-hidden live regions — always mounted so screen
                readers reliably announce toasts. Polite for success/info, assertive
                (interrupts) for errors. The visible bubbles below are presentational. */}
            <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">{politeMsg}</div>
            <div role="alert" aria-live="assertive" aria-atomic="true" className="sr-only">{assertiveMsg}</div>
            {/* z-[1200]: the toast layer must outbid EVERY modal (FilterModal's band is
                z-[1100], AuthModal's backdrop z-[100]) — UI-30's review found the
                migrated FilterModal error toasts painting BENEATH the open modal's
                ink backdrop, invisible to sighted users. The container is
                pointer-events-none (only the bubbles are interactive), so sitting
                above modals steals no clicks. */}
            <div className="fixed top-20 inset-x-0 z-[1200] flex flex-col items-center gap-2 px-4 pointer-events-none">
                {toasts.map((t) => (
                    <Toast
                        key={t.id}
                        severity={t.severity}
                        message={t.message}
                        onDismiss={() => remove(t.id)}
                    />
                ))}
            </div>
        </>
    );
};
